/**
 * Plan Library two-person review (DEV-388) — the pure half, shared with the
 * client so the Plan Library shows exactly what the server enforces.
 *
 * Scope: Plan Library plans only (plans.client_id IS NULL AND agency_id IS
 * NULL). Broker/agency/client plans carry review_status NULL and are never
 * gated (product owner, DEV-388).
 *
 * Status machine (R1):
 *   draft ─submit→ needs_review ─approve→ approved
 *     ↑   ←withdraw─┘      └─reject→ changes_requested ─submit→ needs_review
 * An approved plan is never edited in place (R9): edits become a pending
 * version that walks the same machine while the approved row stays live.
 */
import { normalizeBenefitLines, summarizeBenefitLines } from "./planBenefitLines";

export const PLAN_REVIEW_STATUSES = ["draft", "needs_review", "approved", "changes_requested"] as const;
export type PlanReviewStatus = (typeof PLAN_REVIEW_STATUSES)[number];

export const PLAN_REVIEW_STATUS_LABELS: Record<PlanReviewStatus, string> = {
  draft: "Draft",
  needs_review: "Needs Review",
  approved: "Approved",
  changes_requested: "Changes Requested",
};

export type PlanReviewAction = "submit" | "withdraw" | "approve" | "reject";

/** Allowed transitions. Anything else is a 409. */
export const PLAN_REVIEW_TRANSITIONS: Record<PlanReviewAction, { from: PlanReviewStatus[]; to: PlanReviewStatus }> = {
  submit: { from: ["draft", "changes_requested"], to: "needs_review" },
  withdraw: { from: ["needs_review"], to: "draft" },
  approve: { from: ["needs_review"], to: "approved" },
  reject: { from: ["needs_review"], to: "changes_requested" },
};

export function nextReviewStatus(action: PlanReviewAction, from: PlanReviewStatus | null | undefined): PlanReviewStatus | null {
  const t = PLAN_REVIEW_TRANSITIONS[action];
  return from && t.from.includes(from) ? t.to : null;
}

/** A Plan Library plan: platform-owned, no agency, no client. */
export function isLibraryPlan(plan: { clientId?: number | null; agencyId?: string | null } | null | undefined): boolean {
  return !!plan && plan.clientId == null && plan.agencyId == null;
}

/**
 * May this plan be quoted / linked to a client / enrolled in (R8)?
 * Non-library plans are out of scope and always pass, as do library rows with
 * no review status (created by broker flows — renewal copy, migration import,
 * RFP convert — which must behave as before). A library plan in the review
 * workflow passes only when Approved; a pending version never blocks, the
 * approved row stays live.
 */
export function isPlanApprovedForUse(
  plan: { clientId?: number | null; agencyId?: string | null; reviewStatus?: string | null } | null | undefined,
): boolean {
  if (!plan) return false;
  if (!isLibraryPlan(plan)) return true;
  return plan.reviewStatus == null || plan.reviewStatus === "approved";
}

/** Sections the reviewer must confirm before approving (R6). */
export const REVIEW_SECTIONS = ["details", "rates", "benefits"] as const;
export type ReviewSection = (typeof REVIEW_SECTIONS)[number];

export function missingConfirmations(confirmed: Partial<Record<ReviewSection, boolean>> | null | undefined): ReviewSection[] {
  return REVIEW_SECTIONS.filter((s) => confirmed?.[s] !== true);
}

// ── R2: ready-for-review ──────────────────────────────────────────────────

/**
 * The eight required benefits-summary fields and the plans.raw_benefits line
 * names that satisfy each. Canonical names are the Plan Library editor's
 * fields (shared/constants/wbBenefitFields.ts); the matchers also accept the
 * W&B / legacy spellings summarizeBenefitLines already understands.
 *
 *   Deductible      "Deductible (Individual/Family)", W&B "Medical (Individual/Family)"
 *   Primary Care    "Primary Care Visit" (also "PCP", "Office Visit")
 *   Specialist      "Specialist Visit"
 *   Surgery         "Facility Fee (Ambulatory Surgery Center)" or
 *                   "Physician/Surgeon Fee (Ambulatory Surgery Center)" (any "surgery" line not about the hospital room)
 *   Hospitalization "Facility Fee (Hospital Room)" or "Physician/Surgeon Fee (Hospital Room)"
 *   ER              "Emergency Room Services"
 *   Rx              any of "Rx – Tier 1".."Rx – Tier 4"
 *   OOP Max         "Out-of-Pocket (Individual/Family)"
 *
 * Legacy plan_benefits rows count too: their flattened summary
 * (flattenPlanBenefits → deductible/copay/specialist/surgery/hospital/
 * emergencyRoom/rx*\/outOfPocketMax) is passed in as `legacySummary`.
 */
export const REQUIRED_BENEFIT_FIELDS = [
  { key: "deductible", label: "Deductible" },
  { key: "primaryCare", label: "Primary Care" },
  { key: "specialist", label: "Specialist" },
  { key: "surgery", label: "Surgery" },
  { key: "hospitalization", label: "Hospitalization" },
  { key: "er", label: "ER" },
  { key: "rx", label: "Rx" },
  { key: "oopMax", label: "OOP Max" },
] as const;
export type RequiredBenefitKey = (typeof REQUIRED_BENEFIT_FIELDS)[number]["key"];

/** Summary keys (summarizeBenefitLines / flattenPlanBenefits) that satisfy each field. */
const SUMMARY_KEYS: Record<RequiredBenefitKey, string[]> = {
  deductible: ["deductible"],
  primaryCare: ["copay"],
  specialist: ["specialist"],
  surgery: ["surgery"],
  hospitalization: ["hospital"],
  er: ["emergencyRoom"],
  rx: ["rxGeneric", "rxBrand", "rxTier3", "rxTier4"],
  oopMax: ["outOfPocketMax"],
};

/** Line-name matchers for fields summarizeBenefitLines has no key for. */
function lineSatisfies(key: RequiredBenefitKey, rawName: string): boolean {
  const n = rawName.toLowerCase();
  if (key === "surgery") return /surgery|surgical/.test(n) && !n.includes("hospital");
  if (key === "hospitalization") return n.includes("hospital room") || n.includes("inpatient hospital") || n === "hospitalization";
  return false;
}

/** Labels of the required benefit fields that have no non-blank value. */
export function missingRequiredBenefits(
  rawBenefits: unknown,
  legacySummary?: Record<string, unknown> | null,
): string[] {
  const lines = normalizeBenefitLines(rawBenefits);
  const summary: Record<string, unknown> = { ...(legacySummary ?? {}), ...summarizeBenefitLines(rawBenefits) };
  const has = (v: unknown) => v != null && String(v).trim() !== "";
  return REQUIRED_BENEFIT_FIELDS.filter(({ key }) => {
    if (SUMMARY_KEYS[key].some((k) => has(summary[k]))) return false;
    return !lines.some((l) => has(l.value) && lineSatisfies(key, l.name));
  }).map((f) => f.label);
}

/** Plan types the benefits-summary requirement applies to (medical only — a dental plan has no ER). */
export function requiresBenefitSummary(planType: string | null | undefined): boolean {
  const t = String(planType ?? "").toLowerCase().trim();
  return t === "health" || t === "medical";
}

/** Plan types whose network type (PPO/HMO/...) is required for review readiness. */
export const NETWORK_TYPE_REQUIRED_PLAN_TYPES = ["health", "medical", "dental", "vision"] as const;

export function planTypeNeedsNetworkType(planType: string | null | undefined): boolean {
  const t = String(planType ?? "").toLowerCase().trim();
  return (NETWORK_TYPE_REQUIRED_PLAN_TYPES as readonly string[]).includes(t);
}

export interface ReadinessPlanDetails {
  name?: string | null;
  type?: string | null;
  carrierId?: number | null;
  planCode?: string | null;
  networkType?: string | null;
  metalTier?: string | null;
  effectiveDate?: unknown;
  availableStates?: string[] | null;
}

/**
 * "Complete Plan Details": the identity a broker quotes from — name, type,
 * carrier, plan code, effective date, at least one available state; plus the
 * network type for medical/dental/vision and the metal tier for medical.
 */
export function missingPlanDetails(p: ReadinessPlanDetails): string[] {
  const missing: string[] = [];
  const blank = (v: unknown) => v == null || String(v).trim() === "";
  if (blank(p.name)) missing.push("Plan name");
  if (blank(p.type)) missing.push("Plan type");
  if (p.carrierId == null) missing.push("Carrier");
  if (blank(p.planCode)) missing.push("Plan code");
  if (blank(p.effectiveDate)) missing.push("Effective date");
  if (!p.availableStates || p.availableStates.filter((s) => !blank(s)).length === 0) missing.push("Available states");
  const t = String(p.type ?? "").toLowerCase();
  if (planTypeNeedsNetworkType(t) && blank(p.networkType)) missing.push("Network type");
  if (requiresBenefitSummary(t) && blank(p.metalTier)) missing.push("Metal tier");
  return missing;
}

/**
 * The Plan Builder's "Missing: ..." hint under Create Plan / Save Changes.
 * `blocking` disables the save button; `advisory` lists review-readiness
 * fields (network type) that don't block saving a draft.
 */
export function planBuilderMissingFields(p: {
  name?: string | null;
  type?: string | null;
  carrierId?: number | null;
  planCode?: string | null;
  networkType?: string | null;
}): { blocking: string[]; advisory: string[] } {
  const blank = (v: unknown) => v == null || String(v).trim() === "";
  const blocking: string[] = [];
  if (blank(p.name)) blocking.push("Plan Name");
  if (blank(p.type)) blocking.push("Plan Type");
  if (!p.carrierId) blocking.push("Carrier");
  if (blank(p.planCode)) blocking.push("Plan Code");
  const advisory = planTypeNeedsNetworkType(p.type) && blank(p.networkType) ? ["Network type"] : [];
  return { blocking, advisory };
}

export type SubmitButtonState = "enabled" | "disabled" | "hidden";

/**
 * Submit for Review in the review panel: enabled when the server allows it;
 * shown disabled when submit is the next step (draft / changes requested) but
 * the never-approved plan isn't ready yet; hidden otherwise.
 */
export function submitButtonState(review: {
  canSubmit: boolean;
  targetStatus: PlanReviewStatus | null | undefined;
  readinessBlocking: boolean;
  readiness: { ready: boolean } | null | undefined;
}): SubmitButtonState {
  if (review.canSubmit) return "enabled";
  if (nextReviewStatus("submit", review.targetStatus) == null) return "hidden";
  return review.readinessBlocking && review.readiness && !review.readiness.ready ? "disabled" : "hidden";
}

export interface ReadinessRateRow {
  ratingAreaId?: number | null;
  monthlyRate?: string | number | null;
}

export interface ReadinessResult {
  ready: boolean;
  /** Everything blocking submit, as UI labels ("ER", "Rates (at least one rating area)", ...). */
  missing: string[];
  missingDetails: string[];
  missingBenefits: string[];
  hasRates: boolean;
}

export function checkReadyForReview(input: {
  plan: ReadinessPlanDetails;
  rates: ReadinessRateRow[];
  rawBenefits: unknown;
  legacyBenefitSummary?: Record<string, unknown> | null;
}): ReadinessResult {
  const missingDetails = missingPlanDetails(input.plan);
  const hasRates = input.rates.some((r) => r.monthlyRate != null && Number(r.monthlyRate) > 0);
  const missingBenefits = requiresBenefitSummary(input.plan.type)
    ? missingRequiredBenefits(input.rawBenefits, input.legacyBenefitSummary)
    : [];
  const missing = [
    ...missingDetails,
    ...(hasRates ? [] : ["Rates (at least one rating area)"]),
    ...missingBenefits,
  ];
  return { ready: missing.length === 0, missing, missingDetails, missingBenefits, hasRates };
}

// ── R3: rating-area coverage ─────────────────────────────────────────────

export interface RatingAreaCoverage {
  /** Every rated row is statewide (no rating area) — the plan prices every area. */
  statewide: boolean;
  expected: number[];
  withRates: number[];
  withoutRates: number[];
}

/**
 * Which of the plan's rating areas have rates. `expectedAreaIds` = the areas
 * of the plan's state(s) (geo map). A rate row with no rating area is a
 * statewide price and covers every area.
 */
export function ratingAreaCoverage(rates: ReadinessRateRow[], expectedAreaIds: number[]): RatingAreaCoverage {
  const priced = rates.filter((r) => r.monthlyRate != null && Number(r.monthlyRate) > 0);
  const statewide = priced.length > 0 && priced.some((r) => r.ratingAreaId == null);
  const rated = new Set(priced.filter((r) => r.ratingAreaId != null).map((r) => Number(r.ratingAreaId)));
  const expected = Array.from(new Set(expectedAreaIds.map(Number))).sort((a, b) => a - b);
  const withRates = Array.from(new Set([...expected.filter((a) => statewide || rated.has(a)), ...Array.from(rated)])).sort((a, b) => a - b);
  const withoutRates = statewide ? [] : expected.filter((a) => !rated.has(a));
  return { statewide, expected, withRates, withoutRates };
}

// ── R9: what a pending version changes ───────────────────────────────────

/** One rate cell's identity + value, for change detection. */
export interface RateCell {
  rateStructureType: string;
  coverageTier?: string | null;
  ageBand?: string | null;
  ratingAreaId?: number | null;
  coverageTable?: string | null;
  monthlyRate: string | number;
}

function cellKey(r: RateCell): string {
  return [r.rateStructureType, r.coverageTier ?? "", r.ageBand ?? "", r.ratingAreaId ?? "", r.coverageTable ?? ""].join("|");
}

/** Does a proposed rate table differ from the live one (any cell added, removed or re-priced)? */
export function ratesDiffer(live: RateCell[], proposed: RateCell[]): boolean {
  const norm = (rows: RateCell[]) => {
    const m = new Map<string, number>();
    for (const r of rows) {
      const v = Number(r.monthlyRate);
      if (!Number.isFinite(v) || v <= 0) continue;
      m.set(cellKey(r), Math.round(v * 100));
    }
    return m;
  };
  const a = norm(live);
  const b = norm(proposed);
  if (a.size !== b.size) return true;
  for (const [k, v] of Array.from(a.entries())) if (b.get(k) !== v) return true;
  return false;
}

/** Stable JSON for comparing benefit arrays / field values. */
function stable(v: unknown): string {
  if (v instanceof Date) return JSON.stringify(v.toISOString().slice(0, 10));
  if (Array.isArray(v)) return `[${v.map(stable).join(",")}]`;
  if (v && typeof v === "object") {
    return `{${Object.keys(v as object).sort().map((k) => `${JSON.stringify(k)}:${stable((v as any)[k])}`).join(",")}}`;
  }
  return JSON.stringify(v ?? null);
}

/** Plan fields a pending version may carry; dates compare as YYYY-MM-DD. */
export function fieldsDiffer(live: Record<string, unknown>, proposed: Record<string, unknown>): string[] {
  const asDay = (v: unknown) => (v instanceof Date ? v.toISOString().slice(0, 10) : typeof v === "string" && /^\d{4}-\d{2}-\d{2}/.test(v) ? v.slice(0, 10) : v);
  return Object.keys(proposed).filter((k) => stable(asDay(live[k]) ?? null) !== stable(asDay(proposed[k]) ?? null));
}

export function benefitsDiffer(live: unknown, proposed: unknown): boolean {
  return stable(normalizeBenefitLines(live)) !== stable(normalizeBenefitLines(proposed));
}

/** Event types in plan_review_events (R10). */
export const PLAN_REVIEW_EVENT_LABELS: Record<string, string> = {
  created: "Created",
  edited: "Edited",
  submitted: "Submitted for review",
  withdrawn: "Withdrawn to draft",
  approved: "Approved",
  rejected: "Changes requested",
  pending_created: "Edit to live plan (pending version)",
  pending_updated: "Pending version edited",
  pending_discarded: "Pending version discarded",
  form_attached: "Form attached",
  form_detached: "Form removed",
  live_edited: "Live plan edited",
  grandfathered: "Grandfathered (approved at launch)",
};
